import React from 'react';
import {
  Box,
  Grid,
  Typography,
  Chip,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import { ResponsiveLine } from '@nivo/line';
import { ResponsiveBar } from '@nivo/bar';
import { FornecedorDrillDownData } from '../../types/drillDown';
import { useDataFormatters } from '../../Hooks/useDataFormatters';

interface FornecedorDrillDownContentProps {
  data: FornecedorDrillDownData;
}

export const FornecedorDrillDownContent: React.FC<FornecedorDrillDownContentProps> = ({ data }) => {
  const { fornecedor, compras_por_mes, top_cfops, principais_produtos, transacoes_detalhadas, analise_por_ncm } = data;
  const { formatCurrency, formatNumber, formatCnpjCpf, formatMonthYear } = useDataFormatters();

  if (!fornecedor) return null;

  // Preparar dados para gráfico de linha (evolução das compras)
  const comprasValidas = (compras_por_mes || []).filter(item => 
    item && typeof item.mes === 'number' && typeof item.ano === 'number'
  );
  
  const lineData = [
    {
      id: 'Valor das Compras',
      data: comprasValidas.length > 0 
        ? comprasValidas.map(item => ({
            x: formatMonthYear(item.mes, item.ano),
            y: item.valor_compras || 0,
            documentos: item.documentos || 0,
            transacoes: item.transacoes || 0,
            quantidade: item.quantidade || 0,
            descontos: item.descontos || 0
          }))
        : [{ x: 'Sem dados', y: 0, documentos: 0, transacoes: 0, quantidade: 0, descontos: 0 }]
    },
    {
      id: 'Crédito ICMS',
      data: comprasValidas.length > 0 
        ? comprasValidas.map(item => ({
            x: formatMonthYear(item.mes, item.ano),
            y: item.credito_icms || 0,
            documentos: item.documentos || 0,
            transacoes: item.transacoes || 0
          }))
        : [{ x: 'Sem dados', y: 0, documentos: 0, transacoes: 0 }]
    }
  ];

  // Preparar dados para gráfico de barras (CFOPs)
  const cfopsValidos = (top_cfops || []).filter(item => {
    const temCfop = item && item.cfop;
    const valorNumerico = Number(item.valor_total);
    const temValorTotal = !isNaN(valorNumerico);
    const valorPositivo = valorNumerico > 0;
    
    return temCfop && temValorTotal && valorPositivo;
  });

  const barData = cfopsValidos.map(item => ({
    cfop: item.cfop,
    'Valor Total': item.valor_total,
    'ICMS Total': item.icms_total,
    'Transações': item.transacoes
  }));

  return (
    <Box sx={{ p: 2 }}>
      {/* Informações do fornecedor */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'success.main' }}>
                {fornecedor.nome}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Código:
                </Typography>
                <Chip 
                  label={fornecedor.codigo}
                  color="default"
                  size="small"
                  variant="filled"
                  sx={{ fontWeight: 500 }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                  CNPJ/CPF:
                </Typography>
                <Chip 
                  label={formatCnpjCpf(fornecedor.cnpj_cpf)}
                  color="info"
                  size="small"
                  variant="outlined"
                  sx={{ fontWeight: 500 }}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Local:
                </Typography>
                <Chip 
                  label={`${fornecedor.municipio} - ${fornecedor.uf}`}
                  color="secondary"
                  size="small"
                  variant="outlined"
                />
                {fornecedor.posicao_ranking && (
                  <>
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                      Ranking:
                    </Typography>
                    <Chip 
                      label={`${fornecedor.posicao_ranking}º de ${fornecedor.total_fornecedores}`}
                      color={fornecedor.posicao_ranking <= 5 ? 'success' : fornecedor.posicao_ranking <= 10 ? 'warning' : 'default'}
                      size="small"
                      variant="filled"
                      sx={{ fontWeight: 600 }}
                    />
                  </>
                )}
              </Box>
              {fornecedor.endereco_completo && (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  {fornecedor.endereco_completo}
                </Typography>
              )}
            </Box>
          </Box>
        </Grid>

        {/* Estatísticas financeiras */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h6" color="success.main" sx={{ fontWeight: 700 }}>
                {formatCurrency(fornecedor.total_compras)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Compras
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h6" color="info.main" sx={{ fontWeight: 700 }}>
                {formatCurrency(fornecedor.total_credito_icms)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Crédito ICMS
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h6" color="warning.main" sx={{ fontWeight: 700 }}>
                {formatCurrency(fornecedor.total_descontos)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Descontos
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h6" color="primary.main" sx={{ fontWeight: 700 }}>
                {formatCurrency(fornecedor.ticket_medio)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Ticket Médio
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Gráfico de evolução */}
      {comprasValidas.length > 0 && (
        <Grid item xs={12} sx={{ mb: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Evolução das Compras e Créditos ICMS
              </Typography>
              <Box sx={{ height: 300 }}>
                <ResponsiveLine
                  data={lineData}
                  margin={{ top: 20, right: 20, bottom: 50, left: 60 }}
                  xScale={{ type: 'point' }}
                  yScale={{ type: 'linear', min: 'auto', max: 'auto', stacked: false }}
                  axisTop={null}
                  axisRight={null}
                  axisBottom={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: -45,
                    legend: 'Período',
                    legendOffset: 40,
                    legendPosition: 'middle'
                  }}
                  axisLeft={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0,
                    legend: 'Valor R$',
                    legendPosition: 'middle',
                    legendOffset: -40
                  }}
                  pointSize={10}
                  pointColor={{ theme: 'background' }}
                  pointBorderWidth={2}
                  pointBorderColor={{ from: 'serieColor' }}
                  pointLabelYOffset={-12}
                  useMesh={true}
                  legends={[
                    {
                      anchor: 'top',
                      direction: 'row',
                      justify: false,
                      translateX: 0,
                      translateY: -20,
                      itemsSpacing: 40, // Espaçamento equilibrado
                      itemDirection: 'left-to-right',
                      itemWidth: 100, // Largura compacta (símbolo + texto próximos)
                      itemHeight: 20,
                      itemOpacity: 0.75,
                      symbolSize: 12,
                      symbolShape: 'circle',
                      symbolBorderColor: 'rgba(0, 0, 0, .5)',
                      effects: [
                        {
                          on: 'hover',
                          style: {
                            itemBackground: 'rgba(0, 0, 0, .03)',
                            itemOpacity: 1
                          }
                        }
                      ]
                    }
                  ]}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Top CFOPs */}
      {cfopsValidos.length > 0 && (
        <Grid item xs={12} md={6} sx={{ mb: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top CFOPs Utilizados
              </Typography>
              <Box sx={{ height: 300 }}>
                <ResponsiveBar
                  data={barData}
                  keys={['Valor Total', 'ICMS Total']}
                  indexBy="cfop"
                  margin={{ top: 20, right: 20, bottom: 50, left: 60 }}
                  padding={0.3}
                  groupMode="grouped"
                  valueScale={{ type: 'linear' }}
                  indexScale={{ type: 'band', round: true }}
                  colors={{ scheme: 'nivo' }}
                  borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                  axisTop={null}
                  axisRight={null}
                  axisBottom={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0,
                    legend: 'CFOP',
                    legendPosition: 'middle',
                    legendOffset: 32
                  }}
                  axisLeft={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0,
                    legend: 'Valor R$',
                    legendPosition: 'middle',
                    legendOffset: -40
                  }}
                  labelSkipWidth={12}
                  labelSkipHeight={12}
                  labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                  legends={[
                    {
                      dataFrom: 'keys',
                      anchor: 'top',
                      direction: 'row',
                      justify: false,
                      translateX: 0,
                      translateY: -20,
                      itemsSpacing: 40, // Espaçamento equilibrado
                      itemWidth: 110, // Largura compacta (símbolo + texto próximos)
                      itemHeight: 20,
                      itemDirection: 'left-to-right',
                      itemOpacity: 0.85,
                      symbolSize: 20,
                      effects: [
                        {
                          on: 'hover',
                          style: {
                            itemOpacity: 1
                          }
                        }
                      ]
                    }
                  ]}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Principais produtos */}
      {principais_produtos && principais_produtos.length > 0 && (
        <Grid item xs={12} md={6} sx={{ mb: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Principais Produtos
              </Typography>
              <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Produto</TableCell>
                      <TableCell align="right">Valor</TableCell>
                      <TableCell align="right">ICMS</TableCell>
                      <TableCell align="right">Qtd</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {principais_produtos.slice(0, 10).map((produto, index) => (
                      <TableRow key={index}>
                        <TableCell>{produto.descricao_produto}</TableCell>
                        <TableCell align="right">{formatCurrency(produto.valor_total)}</TableCell>
                        <TableCell align="right">{formatCurrency(produto.icms_total)}</TableCell>
                        <TableCell align="right">{formatNumber(produto.quantidade_total)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Análise por NCM */}
      {analise_por_ncm && analise_por_ncm.length > 0 && (
        <Grid item xs={12} sx={{ mb: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Análise por NCM
              </Typography>
              <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>NCM</TableCell>
                      <TableCell>Descrição</TableCell>
                      <TableCell align="right">Valor Total</TableCell>
                      <TableCell align="right">ICMS Total</TableCell>
                      <TableCell align="right">Quantidade</TableCell>
                      <TableCell align="right">Produtos</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analise_por_ncm.slice(0, 10).map((ncm, index) => (
                      <TableRow key={index}>
                        <TableCell>{ncm.ncm}</TableCell>
                        <TableCell>{ncm.descricao_ncm}</TableCell>
                        <TableCell align="right">{formatCurrency(ncm.valor_total)}</TableCell>
                        <TableCell align="right">{formatCurrency(ncm.icms_total)}</TableCell>
                        <TableCell align="right">{formatNumber(ncm.quantidade_total)}</TableCell>
                        <TableCell align="right">{ncm.produtos_diferentes}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Transações detalhadas */}
      {transacoes_detalhadas && transacoes_detalhadas.length > 0 && (
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Transações Detalhadas (Últimas 20)
              </Typography>
              <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Data</TableCell>
                      <TableCell>Documento</TableCell>
                      <TableCell>CFOP</TableCell>
                      <TableCell>Produto</TableCell>
                      <TableCell align="right">Valor</TableCell>
                      <TableCell align="right">ICMS</TableCell>
                      <TableCell align="right">Qtd</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transacoes_detalhadas.slice(0, 20).map((transacao, index) => (
                      <TableRow key={index}>
                        <TableCell>{transacao.dt_emissao}</TableCell>
                        <TableCell>{transacao.num_doc}</TableCell>
                        <TableCell>{transacao.cfop}</TableCell>
                        <TableCell>{transacao.descr_compl}</TableCell>
                        <TableCell align="right">{formatCurrency(transacao.vl_item)}</TableCell>
                        <TableCell align="right">{formatCurrency(transacao.vl_icms)}</TableCell>
                        <TableCell align="right">{formatNumber(transacao.qtd)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      )}
    </Box>
  );
}; 